/**
 * Find — one box for accounts, contacts and quote / order / invoice numbers (0.14).
 *
 * Ctrl/Cmd+K anywhere, or `/` when not typing. The three sections are three calls
 * fired together; each paints when it answers, so a slow source never holds up the
 * rest (plan budget: first section < 150 ms). A section that FAILS says so — an
 * outage must never read as "no results". Parts and cross-references join in
 * Phase 1. Pattern: isg-intranet components/global-search.js (ui-kit has no Find).
 */
import { debounce, escapeHtml, getIcon, router } from '@isg/ui-kit';
import { reorderBadge } from '../lib/reorder.js';
import { salesApi } from '../services/sales-api.js';

const SECTIONS = [
  { key: 'accounts', label: 'Accounts', call: (q) => salesApi.findAccounts(q) },
  { key: 'parts', label: 'Parts', call: (q) => salesApi.findParts(q), applies: (q) => /\d/.test(q) || q.includes('-') },
  // Something that is NOT our number — a customer's own, a competitor's, a mil-spec —
  // and what it maps to (vault's register). Exact and family matches only.
  { key: 'xrefs', label: 'Also known as', call: (q) => salesApi.findXrefs(q), applies: (q) => /\d/.test(q) && q.length >= 4 },
  { key: 'contacts', label: 'Contacts', call: (q) => salesApi.findContacts(q) },
  { key: 'documents', label: 'Quotes, orders & invoices', call: (q) => salesApi.findDocuments(q),
    applies: (q) => /\d/.test(q) && q.length >= 4 },
];
const ICON = { account: 'users', contact: 'user', quote: 'file-text', order: 'truck', invoice: 'receipt', part: 'box', xref: 'link' };
const HINT = 'Account name or code, a part number (ours, a customer’s or a competitor’s), a contact’s name, email or phone, or a quote, order or invoice number';
const ENTITY = { uk: 'UK', us: 'US', de: 'DE' };

let api = null;

/** Open Find from anywhere (bottom bar, a page's empty state …). */
export function openFind(initial = '') {
  if (api) api.open(initial);
}

export function createFind() {
  const wrapper = document.createElement('div');
  wrapper.className = 'global-search';
  const isMac = /mac/i.test(navigator.platform || '');
  wrapper.innerHTML = `
    <div class="global-search-trigger" tabindex="0" role="button" aria-label="Find (${isMac ? 'Command' : 'Ctrl'}+K)"
         title="Find (${isMac ? '⌘' : 'Ctrl+'}K or /)">
      ${getIcon('search', 16)}
      <span class="global-search-placeholder">Find an account, part, contact or quote…</span>
      <span class="global-search-kbd">${isMac ? '⌘' : 'Ctrl'} K</span>
    </div>
    <div class="global-search-overlay" style="display:none">
      <div class="global-search-modal" role="dialog" aria-modal="true" aria-label="Find">
        <div class="global-search-input-wrapper">
          ${getIcon('search', 18)}
          <input type="text" class="global-search-input" placeholder="Find…" autocomplete="off" spellcheck="false"
                 role="combobox" aria-expanded="true" aria-controls="sc-find-results" enterkeyhint="search">
          <button type="button" class="sc-find-close" aria-label="Close Find" title="Close (Esc)">${getIcon('x', 18)}</button>
        </div>
        <div class="global-search-results" id="sc-find-results" role="listbox"></div>
      </div>
    </div>`;

  const trigger = wrapper.querySelector('.global-search-trigger');
  const overlay = wrapper.querySelector('.global-search-overlay');
  const input = wrapper.querySelector('.global-search-input');
  const results = wrapper.querySelector('.global-search-results');
  // The toolbar is sticky with a z-index, which makes it a stacking context: an
  // overlay left inside it sits BELOW the phone menu button and the page. Mount it
  // on <body> so it covers everything it should.
  document.body.appendChild(overlay);
  let isOpen = false;
  let seq = 0;
  let selected = -1;

  const idle = () => { results.innerHTML = `<div class="sc-find-note" style="padding:var(--space-6) var(--space-5)">${escapeHtml(HINT)}.</div>`; };
  idle();

  function open(initial = '') {
    isOpen = true;
    overlay.style.display = 'flex';
    requestAnimationFrame(() => { overlay.classList.add('active'); input.focus(); });
    if (initial) { input.value = initial; search(initial); }
  }

  function close() {
    isOpen = false;
    seq++;
    overlay.classList.remove('active');
    setTimeout(() => { overlay.style.display = 'none'; input.value = ''; selected = -1; idle(); }, 150);
  }

  function row(item, multiEntity) {
    const badges = [
      reorderBadge(item.reorder),
      ...(multiEntity ? [`<span class="badge badge-gray">${ENTITY[item.entity] || item.entity}</span>`] : []),
      ...(item.badges || []).map(b => `<span class="badge badge-${b === 'my book' ? 'blue' : 'gray'}">${escapeHtml(b)}</span>`),
    ].join('');
    return `
      <div class="global-search-result${item.url ? '' : ' is-static'}" role="option" tabindex="-1"${item.url ? ` data-url="${escapeHtml(item.url)}"` : ' aria-disabled="true"'}>
        <span class="global-search-result-icon">${getIcon(ICON[item.type] || 'file-text', 16)}</span>
        <div class="global-search-result-text">
          <div class="global-search-result-title">${escapeHtml(item.title)}</div>
          <div class="global-search-result-subtitle">${escapeHtml(item.subtitle || '')}</div>
        </div>
        <span class="sc-find-badges">${badges}</span>
      </div>`;
  }

  function paint(state, q) {
    const multi = state.entities > 1;
    let html = '';
    for (const s of SECTIONS) {
      const st = state[s.key];
      if (!st) continue;
      if (st.error) {
        html += `<div class="global-search-category"><div class="global-search-category-label">${s.label}</div>
          <div class="sc-find-note">${getIcon('alert-circle', 14)} Could not search ${s.label.toLowerCase()} just now — this is not "nothing found".</div></div>`;
      } else if (st.items && st.items.length) {
        html += `<div class="global-search-category"><div class="global-search-category-label">${s.label}</div>
          ${st.items.map(i => row(i, multi)).join('')}</div>`;
      }
    }
    const pending = SECTIONS.filter(s => state[s.key] && state[s.key].pending).length;
    if (pending) {
      html += '<div class="global-search-searching"><span class="spinner spinner-sm"></span> Searching…</div>';
    } else if (!html) {
      html = `<div class="global-search-empty"><p style="color:var(--text-tertiary);font-size:var(--text-sm)">
        Nothing found for “${escapeHtml(q)}”.</p><p class="sc-find-note">${escapeHtml(HINT)}.</p></div>`;
    }
    results.innerHTML = html;
    selected = -1;
  }

  const search = debounce((raw) => {
    const q = (raw || '').trim();
    if (q.length < 2) { seq++; idle(); return; }
    const mine = ++seq;
    const state = { entities: 1 };
    for (const s of SECTIONS) {
      if (s.applies && !s.applies(q)) continue;
      state[s.key] = { pending: true };
    }
    paint(state, q);
    for (const s of SECTIONS) {
      if (!state[s.key]) continue;
      s.call(q)
        .then((res) => {
          if (mine !== seq) return;
          state.entities = Math.max(state.entities, ((res.meta || {}).entities || []).length);
          state[s.key] = { items: (res.data || {}).items || [] };
        })
        .catch(() => { if (mine === seq) state[s.key] = { error: true }; })
        .finally(() => { if (mine === seq) paint(state, q); });
    }
  }, 200);

  function go(el) {
    if (!el || !el.dataset.url) return;
    close();
    router.navigate(el.dataset.url);
  }

  trigger.addEventListener('click', () => open());
  trigger.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); } });
  overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });
  overlay.querySelector('.sc-find-close').addEventListener('click', close);
  input.addEventListener('input', () => search(input.value));
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') { close(); return; }
    const rows = [...results.querySelectorAll('.global-search-result')];
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!rows.length) return;
      selected = (selected + (e.key === 'ArrowDown' ? 1 : -1) + rows.length) % rows.length;
      rows.forEach((r, i) => r.classList.toggle('is-active', i === selected));
      rows[selected].scrollIntoView({ block: 'nearest' });
    } else if (e.key === 'Enter') {
      e.preventDefault();
      go(rows[selected] || rows[0]);
    }
  });
  results.addEventListener('click', (e) => go(e.target.closest('.global-search-result')));
  document.addEventListener('keydown', (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
      e.preventDefault();
      if (isOpen) close(); else open();
      return;
    }
    const t = e.target;
    const typing = t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName));
    if (e.key === '/' && !typing && !isOpen && !document.querySelector('.modal-overlay')) {
      e.preventDefault();
      open();
    }
  });

  api = { open, close };
  return wrapper;
}
